import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { v4 } from 'uuid';
import { getFormattedDate, getDate } from '../../../../utils/helpers';
import { ComponentName, getMetaData } from '../../DetailsDrawer/detailsDrawersHelpers';
import { DetailCTARenderer } from '../../UpstackDataGrid/CellRenderers';
import { Text } from '../../../Atoms/Text';
import { handleAssetClick } from '../../UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { RecordLink } from '../../../Atoms/RecordLink';

const matchedFieldHeaderMap: { [key: string]: string } = {
  expectedRpmAccountNumber: 'Expected RPM Account Number',
  supplierOrderNumber: 'Supplier Order Number',
  accountNumber: 'Account Number'
};

const DefaultDefs: GridSingleSelectColDef[] = [
  {
    headerName: 'Customer',
    field: 'assetCustomer',
    flex: 2,
    type: 'singleSelect',
    minWidth: 150,
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <RecordLink to={`/customer/${params.row.assetCustomerId}`}>
        <div className="flex items-center text-small">{params.row.assetCustomer}</div>
      </RecordLink>
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetNumber',
    headerName: 'Asset',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    minWidth: 150,
    flex: 1,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      if (!params.row.assetNumber) return <></>;
      const type = params.row.assetSfId ? ComponentName.ASSET : ComponentName.PROVISIONAL_ASSET;
      return (
        <button
          className="flex items-center"
          type="button"
          onClick={() => handleAssetClick(params.row.assetId, type)}>
          <Text
            size="sm14"
            color="indigo">
            {params.row.assetNumber as string}
          </Text>
        </button>
      );
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetActiveLinks',
    headerName: 'Active Links',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    minWidth: 100,
    flex: 1,
    filterable: false
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetCommissionStatus',
    headerName: 'Commissions Status',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column'
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetTsd',
    headerName: 'TSD',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: true,
    minWidth: 150,
    flex: 1,
    description: 'Drag to move column'
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAccountNumber',
    headerName: 'RPM Account',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    minWidth: 170,
    flex: 2,
    filterable: false,
    valueFormatter: (value, row) => row.rpmAccountNumber,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={params.row.rpmAccountNumber}
        valueFormatted={''}
        state={{
          id: '',
          type: 'rpmAccount',
          data: params.row,
          ...getMetaData({ name: ComponentName.RPM_ACCOUNT, id: params.row.rpmAccountId })
        }}
      />
    )
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAccountActiveLinks',
    headerName: 'Active Links',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    minWidth: 100,
    flex: 1,
    filterable: false
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmAccountSupplierName',
    headerName: 'RPM Supplier',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: true,
    minWidth: 170,
    flex: 2,
    description: 'Drag to move column'
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'rpmSource',
    headerName: 'Source',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: true,
    minWidth: 150,
    flex: 2,
    description: 'Drag to move column'
  }
];

const PreviewDefs: GridSingleSelectColDef[] = [...DefaultDefs];

const ExistingDefs: GridSingleSelectColDef[] = [
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'details',
    headerName: '',
    sortable: false,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({
            name: ComponentName.LINK,
            id: params.row.id,
            data: params.row
          })
        }}
      />
    ),
    hideable: false
  },
  {
    headerName: 'Link Status',
    field: 'status',
    type: 'singleSelect',
    minWidth: 100,
    flex: 1,
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    valueFormatter: (value, row) => {
      if (row.archived) return 'Archived';
      if (row.candidate) return 'Candidate';
      return 'Active';
    }
  },
  ...DefaultDefs
];

const NotFoundDefs = (matchedField: string): GridSingleSelectColDef[] => [
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetNumber',
    headerName: 'Asset',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    minWidth: 150,
    flex: 1,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      if (!params.row.assetNumber) return <></>;
      const type = params.row.assetSfId ? ComponentName.ASSET : ComponentName.PROVISIONAL_ASSET;
      return (
        <button
          className="flex items-center"
          onClick={() => handleAssetClick(params.row.assetId, type)}>
          <Text
            size="sm14"
            color="indigo">
            {params.row.assetNumber as string}
          </Text>
        </button>
      );
    }
  },
  {
    headerName: 'Customer',
    field: 'assetCustomer',
    flex: 1,
    type: 'singleSelect',
    minWidth: 150,
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <RecordLink to={`/customer/${params.row.assetCustomerId}`}>
        <div className="flex items-center text-small">{params.row.assetCustomer}</div>
      </RecordLink>
    )
  },
  {
    headerName: matchedFieldHeaderMap[matchedField] || 'Matched Field',
    field: matchedField,
    type: 'singleSelect',
    minWidth: 100,
    flex: 1,
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetExpectedFirstCommissionRun',
    headerName: 'Expected First Commission Run',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false,
    minWidth: 150,
    flex: 1,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    }
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetCommissionStatus',
    headerName: 'Commissions Status',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    filterable: false,
    minWidth: 150,
    flex: 1
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'assetActiveLinks',
    headerName: 'Active Links',
    sortable: true,
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    minWidth: 100,
    flex: 1,
    filterable: false
  }
];

const ConflictsDefs = (matchedField: string): GridSingleSelectColDef[] => [...NotFoundDefs(matchedField)];

export const getColumnDefsByKey = (key: string, matchedField: string): GridSingleSelectColDef[] => {
  switch (key) {
    case 'new':
      return PreviewDefs;
    case 'existing':
      return ExistingDefs;
    case 'success':
      return ExistingDefs;
    case 'notFound':
      return NotFoundDefs(matchedField);
    case 'conflicts':
      return ConflictsDefs(matchedField);
    default:
      return DefaultDefs;
  }
};
